import { Client } from "@/Common/Api/Client";
import { SearchIndividualResponse } from "@/Common/Api/Individuals/Dto/SearchIndividualResponse";
import { BaseSearchAutocompleteCustom, BaseSearchStrategy } from "@/Common/Components/Search/BaseSearchAutocompleteCustom";
import { Person, PersonSourceContext } from "@/Common/Models/Person/Person";
import { PersonDropdownItem } from "@/Common/Models/UI/Inputs/PersonDropdownItem";
import * as ServerOperations from "@/Common/Operations/operations.server";

import img_icon_bluestart from "@/Common/_images/Icons/Ramq/BlueStart.png";

class PersonSearchAutocompleteCustom extends BaseSearchAutocompleteCustom<Person> {
    protected defaultSearchStrategy = new PatientSearchStrategy(ServerOperations.Server.Myle_SearchIndividual);

    protected renderLabel(model: Person): string {
        return model?.NameWithIdentifier;
    }

    protected searchStrategyRenderItem(model: Person, cssClass: string = ""): React.ReactNode {
        if (model.IsInactive || model.SourceContext) {
            return (
                <div className={`flex-container ${cssClass}`}>
                    {model.IsInactive && (
                        <div className="flex-container">
                            <div><span style={{ color: "red", paddingRight: "5px" }}>{cacheStore.GetString("DeactivatedLabel")}</span></div>
                            <div>{" - "}</div>
                        </div>
                    )}
                    <div style={{ overflow: "hidden", width: "100%" }}>
                        <span>{this.renderLabel(model)}</span>
                    </div>
                    {model.SourceContext && (
                        <div className="flex-container" style={{ minWidth: "30px", justifyContent: "flex-end", paddingRight: "5px" }}>
                            {model.SourceContext == PersonSourceContext.IsIntervenorOnFile &&
                                <div><img className="margin-xs-left" style={{ width: "12px" }} src={img_icon_bluestart} /> </div>
                            }
                            {model.SourceContext == PersonSourceContext.IsRamqIntervenor &&
                                <div>[RAMQ]</div>
                            }
                            {model.SourceContext == PersonSourceContext.HasLastMedicalNote &&
                                <div>[]</div>
                            }
                        </div>
                    )}
                </div>
            );
        }

        return <div className={cssClass}><span>{this.renderLabel(model)}</span></div>;
    }

    protected getItemId(model: Person): string {
        return model?.ContactId;
    }

    protected getListStyle(): React.CSSProperties {
        return this.searchStrategy instanceof PatientSearchStrategy ? { height: "27px", display: "flex" } : super.getListStyle();
    }
}

class ClinicianSearchStrategy extends BaseSearchStrategy<Person> {
    public id = "clinicianSearch";
    public cliniciansToSearchFrom: Person[];

    constructor(clinicians: Person[]) {
        super();
        this.cliniciansToSearchFrom = clinicians.filter(p => p.IsInactive == false);
    }

    public async executeSearch(searchText: string): Promise<PersonDropdownItem[]> {
        let results: PersonDropdownItem[] = [];
        if (!searchText) {
            results = this.cliniciansToSearchFrom
                .map((p, index) => p.toPersonDropdownItem(index === 0));
        }
        else {
            results = this.cliniciansToSearchFrom
                .map(p => p.ToSearchableItem(true))
                .filterSearch(searchText, p => p.Value_Searchable)
                .sortByPropertyDesc(p => p.Value)
                .sortByProperty(p => p.Model.IsInactive)
                .map((p, idx) => p.Model.toPersonDropdownItem(idx === 0));
        }

        return new Promise((resolve => resolve(results.sortByProperty(p => p.Item.LastName?.trim(), false))));
    }
}

class CustomSortClinicianSearchStrategy extends BaseSearchStrategy<Person> {
    public id: string = "customSortClinicianSearch";
    public cliniciansToSearchFrom: Person[];

    constructor(clinicians: Person[]) {
        super();
        this.cliniciansToSearchFrom = clinicians.filter(p => !p.IsInactive);
    }

    public async executeSearch(searchText: string): Promise<PersonDropdownItem[]> {
        let results: PersonDropdownItem[] = [];
        if (!searchText) {
            results = this.cliniciansToSearchFrom
                .map((p, index) => p.toPersonDropdownItem(index === 0));
        }
        else {
            results = this.cliniciansToSearchFrom
                .map(p => p.ToSearchableItem(true))
                .filterSearch(searchText, p => p.Value_Searchable)
                .sortByPropertyDesc(p => p.Value)
                .map((p, idx) => p.Model.toPersonDropdownItem(idx === 0));
        }

        return Promise.resolve(results);
    }
}

class PatientSearchStrategy extends BaseSearchStrategy<Person> {
    public id = "patientSearch";
    private searchOperation: number;
    private minQueryLengthSearchChars: number;
    private maxResultsToReturn: number;
    private requestId: number;
    private lastResults: PersonDropdownItem[];

    constructor(operation: number) {
        super();
        this.searchOperation = operation;
        this.minQueryLengthSearchChars = 2;
        this.maxResultsToReturn = 30;
        this.lastResults = [];
    }

    public async executeSearch(searchText: string): Promise<PersonDropdownItem[]> {
        if (searchText == null || searchText.length < this.minQueryLengthSearchChars)
            return null;

        this.requestId = sessionStore.newId();
        const response = await Client.postAsync<SearchIndividualResponse>(
            this.searchOperation,
            {
                Query: searchText,
                IsInactive: false,
                ResultsToReturn: this.maxResultsToReturn,
                IncludeRelatives: false,
                IncludePatients: true,
                OrderedResponseId: this.requestId
            }
        );

        if (!response.isSuccess)
            return this.lastResults;

        if (this.requestId === response.data.OrderedResponseId)
            this.lastResults = response.data.SearchIndividualResults
                .map((p, idx) => Person
                    .FromSearchIndividualResult(p)
                    .toPersonDropdownItem(idx === 0)
                );

        return this.lastResults;
    }
}

export { ClinicianSearchStrategy, CustomSortClinicianSearchStrategy, PatientSearchStrategy, PersonSearchAutocompleteCustom };
